import { useMemo } from 'react';
import { DateTime, Duration } from 'luxon';
import { UserProfile } from '@auth0/nextjs-auth0';
import {
  Opening,
  Filters,
  ExternalCalendarEventSummary,
  Reservation,
  TimeZone,
  SelectedLocation,
  FlexCalSummary,
  ExternalCalendar,
} from '../../utils/types';
import isBookedExternally from '../../utils/helpers/isBookedExternally';
import isBookedInternally from '../../utils/helpers/isBookedInternally';
import { useCalendarFilters } from './useCalendarFilters';

interface UseHasAvailableEventsProps {
  flexCal: FlexCalSummary;
  flexCalOpenings: Opening[];
  externalEvents: ExternalCalendarEventSummary[];
  participantEvents?: ExternalCalendarEventSummary[];
  externalCalendars?: ExternalCalendar[];
  reservations: Reservation[];
  filters: Filters;
  timeZone: TimeZone;
  selectedLocation: SelectedLocation;
  user?: UserProfile;
  startDate: string;
  endDate: string;
}

export const useHasAvailableEvents = (props: UseHasAvailableEventsProps) => {
  const {
    flexCal,
    flexCalOpenings,
    externalEvents,
    participantEvents,
    reservations,
    filters,
    timeZone,
    selectedLocation,
    startDate,
    endDate,
  } = props;

  const {
    filterByMeetingLength,
    filterByPhysicalLocation,
    filterByVirtualLocation,
    getDurationInMinutes,
  } = useCalendarFilters(filters);

  const hasAvailableEvent = useMemo(() => {
    const calculationStart = DateTime.fromISO(startDate);
    const calculationEnd = DateTime.fromISO(endDate);
    const now = DateTime.now();
    const bookingBufferTime = now
      .plus({ hours: flexCal?.firstAvailableTimeInHours ?? 0 })
      .toMillis();

    for (const opening of flexCalOpenings) {
      const openingStart = DateTime.fromISO(opening.startDate);
      const openingEnd = DateTime.fromISO(opening.endDate);

      if (openingEnd <= calculationStart || openingStart >= calculationEnd)
        continue;

      const locationFilteredOut =
        filterByPhysicalLocation(
          opening.physicalLocations,
          filters,
          selectedLocation
        ) &&
        filterByVirtualLocation(
          opening.virtualLocations,
          filters,
          selectedLocation
        );
      if (locationFilteredOut) continue;

      const openingDuration = openingEnd.diff(openingStart, 'minutes');

      for (const duration of opening.durations || []) {
        const durationAsMin = getDurationInMinutes(duration);
        if (filterByMeetingLength(durationAsMin, filters)) continue;

        const slotDuration = Duration.fromISOTime(duration);
        const totalSlots = Math.floor(
          openingDuration.as('minutes') / slotDuration.as('minutes')
        );
        let slotStart = openingStart;

        for (let i = 0; i < totalSlots; i++) {
          const slotEnd = slotStart.plus(slotDuration);

          if (slotEnd <= calculationStart || slotStart >= calculationEnd) {
            slotStart = slotEnd;
            continue;
          }

          const isReserved = opening.reservations?.some((res) => {
            const resStart = DateTime.fromISO(res.startTime).toMillis();
            const resEnd = DateTime.fromISO(res.endTime).toMillis();
            return (
              slotStart.toMillis() < resEnd && slotEnd.toMillis() > resStart
            );
          });
          if (isReserved) {
            slotStart = slotEnd;
            continue;
          }

          if (slotStart.toMillis() < bookingBufferTime) {
            slotStart = slotEnd;
            continue;
          }

          const eventStartZoned = slotStart.setZone(timeZone.tzdbId);
          const eventEndZoned = slotEnd.setZone(timeZone.tzdbId);

          if (
            isBookedExternally(
              eventStartZoned,
              eventEndZoned,
              externalEvents
            ) ||
            isBookedInternally(eventStartZoned, eventEndZoned, reservations)
          ) {
            slotStart = slotEnd;
            continue;
          }

          // Found at least one available slot!
          return true;
        }
      }
    }

    return (
      participantEvents?.some((event) => {
        const eventStart = DateTime.fromISO(event.startDate);
        const eventEnd = DateTime.fromISO(event.endDate);
        return eventEnd > calculationStart && eventStart < calculationEnd;
      }) ?? false
    );
  }, [
    flexCal,
    flexCalOpenings,
    externalEvents,
    participantEvents,
    reservations,
    filters,
    timeZone,
    selectedLocation,
    startDate,
    endDate,
    filterByMeetingLength,
    filterByPhysicalLocation,
    filterByVirtualLocation,
    getDurationInMinutes,
  ]);

  return { hasAvailableEvent };
};
